/**
 * The names of the sections a lesson is read in — one table, seven surfaces.
 *
 * Canonical copy lives at `backend/services/lesson_sections.py`; this file and
 * `mobile/src/utils/lessonSections.js` mirror it, and
 * `backend/tests/test_lesson_sections.py` reads all three and fails on drift.
 * Keep them in step.
 *
 * A lesson renders in seven places (instructor + student portal, on web and on
 * iOS, plus the summary email and the exported PDF) and every one of them used
 * to type its own heading. That gave one section four names — "Practice this
 * week", "Practice items", "Practice This Week", "Practice Items" — and the
 * summary two ("Summary" and "Overview"). A heading is the kind of string
 * nobody greps for, which is why it drifted quietly.
 *
 * Casing was never the visible part: every surface uppercases its headings in
 * CSS, so what a family actually saw differ was the WORDING — "OVERVIEW" in
 * the email against "SUMMARY" in the portal, for the same paragraph.
 *
 * Audience is the one distinction worth keeping. An instructor reads "Student
 * interests & requests"; the family reads "Your interests & requests" — one
 * section, correctly addressed to two readers. So a label is a pair, and a
 * section with nothing to vary repeats itself.
 *
 * Labels are PLAIN TEXT. The email and the PDF interpolate into HTML and
 * escape at the call site; storing "&amp;" here would make React render a
 * literal ampersand entity.
 */

export const AUDIENCES = ["instructor", "family"];

export const LESSON_SECTION_LABELS = {
  summary: { instructor: "Summary", family: "Summary" },
  // What this student is working toward. Replaced `key_elements` (which
  // described the LESSON) and absorbed `student_requests`, so there is one
  // section about the student rather than two overlapping ones.
  student_focus: { instructor: "Student focus", family: "Your focus" },
  key_elements: { instructor: "Key elements", family: "Key elements" },
  practice_items: { instructor: "Practice this week", family: "Practice this week" },
  clips: { instructor: "Practice clips", family: "Practice clips" },
  core_concepts: { instructor: "Core concepts", family: "Core concepts" },
  student_requests: {
    instructor: "Student interests & requests",
    family: "Your interests & requests",
  },
  // "Assignment" would collide with the Spec 01 practice-assignment feature,
  // which is a different thing on the same page.
  instructor_notes: {
    instructor: "Instructor notes",
    family: "Notes from your teacher",
  },
  attachments: { instructor: "Attachments", family: "Attachments" },
  // Two names for one list because the readers did two different things: the
  // student submitted a recording, the instructor reviews the submission.
  practice_feedback: {
    instructor: "Practice submissions",
    family: "Practice feedback",
  },
};

/**
 * The canonical reading order of a lesson, and the second half of what this
 * module exists for. Order used to live in each surface"s JSX, which is the
 * same failure the labels had: seven files free to disagree, and no way to
 * change the order in one place.
 *
 * The linear surfaces (the email, both PDF exports and both student views)
 * RENDER from this list, so a reorder here moves them all. The two instructor
 * surfaces are two-column and heavily interleaved with edit mode, so they keep
 * their JSX and are GUARDED instead: `backend/tests/test_lesson_sections.py`
 * asserts the order each one actually renders is a subsequence of this list.
 *
 * Sections a lesson has nothing for are simply skipped, which is what lets the
 * two retired keys sit here harmlessly: a lesson recorded before Student Focus
 * still renders its Key elements in the slot it always occupied, and a new
 * lesson never shows either.
 */
export const LESSON_SECTION_ORDER = [
  "summary",
  "student_focus",
  // Retired, and placed where it used to sit so an older lesson reads exactly
  // as it always did.
  "key_elements",
  "practice_items",
  "instructor_notes",
  "clips",
  "attachments",
  "core_concepts",
  // Retired — absorbed by student_focus. Same reasoning as key_elements.
  "student_requests",
  "practice_feedback",
];

/**
 * Retired sections: never written for a new lesson, still rendered when an
 * older one carries content.
 */
export const RETIRED_SECTIONS = ["key_elements", "student_requests"];

/**
 * The given section keys in canonical reading order.
 *
 * Pass nothing for the whole order; pass a subset (a column of a two-column
 * layout, say) to sort just those. Throws on an unknown key rather than
 * sorting it to the end, for the reason `sectionLabel` throws: a silently
 * misplaced section is not something anyone would notice in review.
 *
 * @param {string[]} [keys]
 * @returns {string[]}
 */
export function orderedSections(keys) {
  if (!keys) return [...LESSON_SECTION_ORDER];
  const wanted = new Set(keys);
  const unknown = [...wanted].filter((k) => !LESSON_SECTION_ORDER.includes(k));
  if (unknown.length) throw new Error(`unknown lesson section(s): ${unknown.sort().join(", ")}`);
  return LESSON_SECTION_ORDER.filter((k) => wanted.has(k));
}

/**
 * The heading for `key` as `audience` reads it.
 *
 * Throws on an unknown key or audience rather than falling back to the key:
 * a silent fallback would put an internal field name ("student_requests") in
 * front of a parent, and nothing would fail loudly enough to notice.
 *
 * @param {string} key - Section key, e.g. "practice_items".
 * @param {"instructor"|"family"} [audience="family"]
 * @returns {string}
 */
export function sectionLabel(key, audience = "family") {
  const labels = LESSON_SECTION_LABELS[key];
  if (!labels) throw new Error(`unknown lesson section: ${key}`);
  const label = labels[audience];
  if (!label) throw new Error(`unknown audience: ${audience}`);
  return label;
}
